import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, query, where, orderBy, Timestamp, getDoc, getDocFromServer, serverTimestamp, setDoc, deleteField } from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';
import { triggerNewLeadNotification, triggerNewClientNotification } from './notificationTriggerService';
import { CONTACT_FLAG_THRESHOLD_DAYS } from '../utils/contactFlags';

const ACCOUNTS_COLLECTION = 'accounts';

/**
 * Photo shown in CRM for the account primary contact: internal CRM upload wins, then portal profile sync, then legacy photoURL.
 * Portal must not use this for client-facing UI — clients use users.profileImage (and team profile images).
 */
export function getCrmContactDisplayPhotoUrl(account) {
  const c = account?.contact || {};
  return c.crmPhotoURL || c.portalPhotoURL || c.photoURL || '';
}

/**
 * Logo shown in CRM: internal CRM upload wins, then portal-synced companyLogo (visible on client profile).
 * Client portal must only read account.companyLogo / users.companyLogo — never crmCompanyLogo.
 */
export function getCrmCompanyLogoDisplayUrl(account) {
  return account?.crmCompanyLogo || account?.companyLogo || '';
}

/**
 * CRM-only headshot for a portal user (users doc id) on this account; does not change users.profileImage.
 * Falls back to portal profile in UI via caller (e.g. || member.profileImage).
 */
export function getCrmTeamMemberDisplayPhotoUrl(account, portalUserId) {
  if (!portalUserId) return '';
  const map = account?.crmTeamMemberPhotos;
  if (!map || typeof map !== 'object') return '';
  return map[portalUserId] || '';
}

export const getAccounts = async () => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to get accounts');
    }

    const accountsRef = collection(db, ACCOUNTS_COLLECTION);
    const q = query(
      accountsRef, 
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      // Convert Firestore Timestamps to JavaScript Dates
      contractStart: doc.data().contractStart?.toDate() || null,
      contractEnd: doc.data().contractEnd?.toDate() || null,
      createdAt: doc.data().createdAt?.toDate() || null,
      updatedAt: doc.data().updatedAt?.toDate() || null
    }));
  } catch (error) {
    console.error('Error getting accounts:', error);
    throw error;
  }
};

export const addAccount = async (accountData) => {
  try {
    console.log('addAccount called with data:', accountData);
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to add accounts');
    }
    
    const accountsRef = collection(db, ACCOUNTS_COLLECTION);
    
    // Convert all fields to proper Firestore data types
    const formattedData = {
      type: String(accountData.type),
      stage: String(accountData.stage),
      substatus: accountData.substatus ? String(accountData.substatus) : null,
      company: String(accountData.company),
      contact: {
        firstName: String(accountData.contact?.firstName || ''),
        lastName: String(accountData.contact?.lastName || ''),
        email: String(accountData.contact?.email || ''),
        phone: String(accountData.contact?.phone || ''),
        title: String(accountData.contact?.title || ''),
        ...(accountData.contact?.photoURL && { photoURL: String(accountData.contact.photoURL) }),
        ...(accountData.contact?.crmPhotoURL && { crmPhotoURL: String(accountData.contact.crmPhotoURL) }),
        ...(accountData.contact?.portalPhotoURL && { portalPhotoURL: String(accountData.contact.portalPhotoURL) })
      },
      address: String(accountData.address || ''),
      city: String(accountData.city || ''),
      state: String(accountData.state || ''),
      zipCode: String(accountData.zipCode || ''),
      country: String(accountData.country || ''),
      website: String(accountData.website || ''),
      estimatedRevenue: Number(accountData.estimatedRevenue) || 0,
      leadSource: String(accountData.leadSource || 'Website'),
      subscribedToMarketingEmails: Boolean(accountData.subscribedToMarketingEmails),
      contractStart: accountData.contractStart ? Timestamp.fromDate(new Date(accountData.contractStart)) : null,
      contractEnd: accountData.contractEnd ? Timestamp.fromDate(new Date(accountData.contractEnd)) : null,
      projectIds: Array.isArray(accountData.projectIds) ? accountData.projectIds : [],
      notes: Array.isArray(accountData.notes) ? accountData.notes : [],
      createdAt: serverTimestamp(),
      lastActivityAt: serverTimestamp()
    };

    console.log('Formatted data for Firestore:', formattedData);
    const docRef = await addDoc(accountsRef, formattedData);
    
    // Enhanced logging of the new document ID
    console.log('✅ New account created successfully!');
    console.log('📄 Document ID:', docRef.id);
    console.log('🔗 Full document path:', `${ACCOUNTS_COLLECTION}/${docRef.id}`);
    
    // Trigger notification based on account type
    const accountWithId = {
      id: docRef.id,
      ...formattedData,
      substatus: formattedData.substatus || accountData.substatus || null,
      // Convert Timestamps back to Dates for the UI
      contractStart: formattedData.contractStart?.toDate() || null,
      contractEnd: formattedData.contractEnd?.toDate() || null,
      createdAt: new Date(), // Use current date for UI since serverTimestamp() isn't available client-side
      lastActivityAt: new Date() // Use current date for UI since serverTimestamp() isn't available client-side
    };

    // Trigger appropriate notification
    if (accountData.type === 'Leads') {
      await triggerNewLeadNotification(accountWithId);
    } else if (accountData.type === 'Clients') {
      await triggerNewClientNotification(accountWithId);
    }
    // Note: Zapier webhook will be triggered automatically by Firestore trigger if it's a Lead in "Lead Received" stage
    
    return accountWithId;
  } catch (error) {
    console.error('Error in addAccount:', error);
    throw error;
  }
};

// Helper function to recursively remove undefined values from objects
const removeUndefinedValues = (obj) => {
  if (obj === null || obj === undefined) {
    return null;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefinedValues(item));
  }
  
  if (typeof obj === 'object' && obj.constructor === Object) {
    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = removeUndefinedValues(value);
      }
    }
    return cleaned;
  }
  
  return obj;
};

export const updateAccount = async (accountId, accountData) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to update accounts');
    }

    const accountRef = doc(db, ACCOUNTS_COLLECTION, accountId);
    
    // Convert dates to Firestore Timestamps, but only if they're valid dates
    let formattedData = {
      ...accountData,
      updatedAt: Timestamp.fromDate(new Date())
    };

    // Only add contractStart if it's a valid date
    if (accountData.contractStart) {
      const contractStartDate = new Date(accountData.contractStart);
      if (!isNaN(contractStartDate.getTime())) {
        formattedData.contractStart = Timestamp.fromDate(contractStartDate);
      }
    }

    // Only add contractEnd if it's a valid date
    if (accountData.contractEnd) {
      const contractEndDate = new Date(accountData.contractEnd);
      if (!isNaN(contractEndDate.getTime())) {
        formattedData.contractEnd = Timestamp.fromDate(contractEndDate);
      }
    }

    // Remove undefined values recursively before saving to Firestore
    formattedData = removeUndefinedValues(formattedData);

    await updateDoc(accountRef, formattedData);
    return { id: accountId, ...formattedData };
  } catch (error) {
    console.error('Error updating account:', error);
    throw error;
  }
};

export const deleteAccount = async (accountId) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to delete accounts');
    }

    const accountRef = doc(db, ACCOUNTS_COLLECTION, accountId);
    await deleteDoc(accountRef);
    return accountId;
  } catch (error) {
    console.error('Error deleting account:', error);
    throw error;
  }
};

export const getAccountsByType = async (type) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to get accounts');
    }

    const accountsRef = collection(db, ACCOUNTS_COLLECTION);
    const q = query(
      accountsRef, 
      where('type', '==', type)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      // Convert Firestore Timestamps to JavaScript Dates
      contractStart: doc.data().contractStart?.toDate() || null,
      contractEnd: doc.data().contractEnd?.toDate() || null,
      createdAt: doc.data().createdAt?.toDate() || null,
      updatedAt: doc.data().updatedAt?.toDate() || null
    }));
  } catch (error) {
    console.error('Error getting accounts by type:', error);
    throw error;
  }
};

export const updateAccountStage = async (accountId, newStage) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to update accounts');
    }

    const accountRef = doc(db, ACCOUNTS_COLLECTION, accountId);
    
    // Get current account data to check previous stage
    const accountSnap = await getDoc(accountRef);
    if (!accountSnap.exists()) {
      throw new Error('Account not found');
    }
    
    const currentAccount = accountSnap.data();
    const previousStage = currentAccount.stage;
    
    // Only set date if moving to a different stage
    const isMovingToNewStage = previousStage !== newStage;
    const updateData = {
      stage: newStage,
      updatedAt: Timestamp.fromDate(new Date())
    };
    
    // Set the date for the new stage when entering it (only if moving to a new stage)
    if (isMovingToNewStage) {
      const newStageLower = newStage.toLowerCase();
      const currentDate = new Date().toISOString();
      const now = new Date();
      
      // Always set the date when entering a stage (captures when they entered that stage)
      if (newStageLower.includes('lead received') || newStageLower.includes('received')) {
        // Only set if not already set (Lead Received might already have a date from creation)
        if (!currentAccount.receivedDate) {
          updateData.receivedDate = Timestamp.fromDate(now);
          console.log('Set receivedDate to:', updateData.receivedDate);
        }
      } else if (newStageLower.includes('discovery')) {
        // Always set/update when entering Discovery Call stage
        updateData.discoveryCallDate = currentDate;
        console.log('Set discoveryCallDate to:', currentDate);
      } else if (newStageLower.includes('assessment')) {
        // Always set/update when entering Assessment stage
        updateData.discoveryCompletedAt = currentDate;
        console.log('Set discoveryCompletedAt to:', currentDate);
      } else if (newStageLower.includes('onboarding')) {
        // Always set/update when entering Onboarding Call stage
        updateData.onboardingCallDate = currentDate;
        console.log('Set onboardingCallDate to:', currentDate);
      } else if (newStageLower.includes('proposal')) {
        // Always set/update when entering Proposal stage
        updateData.proposalCallDate = currentDate;
        console.log('Set proposalCallDate to:', currentDate);
      }
    }
    
    await updateDoc(accountRef, updateData);

    return accountId;
  } catch (error) {
    console.error('Error updating account stage:', error);
    throw error;
  }
};

export const getAccountById = async (id) => {
  try {
    const docRef = doc(db, ACCOUNTS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    } else {
      throw new Error('Account not found');
    }
  } catch (error) {
    console.error('Error getting account:', error);
    throw error;
  }
};

/** Update only customProgramDeliverableQuantities (merge one item's quantity). Ensures the field persists. */
export const updateAccountDeliverableQuantity = async (accountId, itemId, quantity) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to update accounts');
    }
    const accountRef = doc(db, ACCOUNTS_COLLECTION, accountId);
    const snap = await getDoc(accountRef);
    if (!snap.exists()) throw new Error('Account not found');
    const current = snap.data();
    const quantities = { ...(current.customProgramDeliverableQuantities || {}), [itemId]: quantity };
    const update = {
      customProgramDeliverableQuantities: quantities,
      updatedAt: Timestamp.fromDate(new Date())
    };
    // A quantity lives in exactly ONE map. Clear any stale deliverableQuantityOverrides entry for
    // this id so it can't shadow the value (fetchProgramDeliverablesBundle applies overrides on top
    // of everything). Stale entries arise when an item edited pre-Custom is later moved into the
    // custom deliverables list. Guard the dotted path: Firestore doc ids never contain '.'.
    if (
      current.deliverableQuantityOverrides &&
      Object.prototype.hasOwnProperty.call(current.deliverableQuantityOverrides, itemId) &&
      !String(itemId).includes('.')
    ) {
      update[`deliverableQuantityOverrides.${itemId}`] = deleteField();
    }
    await updateDoc(accountRef, update);
    return quantities;
  } catch (error) {
    console.error('Error updating deliverable quantity:', error);
    throw error;
  }
};

/** Update only deliverableQuantityOverrides (merge one item's quantity). For template programs. */
export const updateAccountDeliverableQuantityOverride = async (accountId, itemId, quantity) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to update accounts');
    }
    const accountRef = doc(db, ACCOUNTS_COLLECTION, accountId);
    const snap = await getDoc(accountRef);
    if (!snap.exists()) throw new Error('Account not found');
    const current = snap.data();
    const overrides = { ...(current.deliverableQuantityOverrides || {}), [itemId]: quantity };
    const update = {
      deliverableQuantityOverrides: overrides,
      updatedAt: Timestamp.fromDate(new Date())
    };
    // Symmetric single-source-of-truth: if this id also had a customProgramDeliverableQuantities
    // entry, clear it so the two maps can't disagree for the same item.
    if (
      current.customProgramDeliverableQuantities &&
      Object.prototype.hasOwnProperty.call(current.customProgramDeliverableQuantities, itemId) &&
      !String(itemId).includes('.')
    ) {
      update[`customProgramDeliverableQuantities.${itemId}`] = deleteField();
    }
    await updateDoc(accountRef, update);
    return overrides;
  } catch (error) {
    console.error('Error updating deliverable quantity override:', error);
    throw error;
  }
};

/**
 * Add a one-period-only deliverable to an account, pinned to a specific billing-period key.
 * Upserts one entry per (itemId, periodKey) and ADDS to any existing quantity for that period,
 * so repeated adds accumulate. The entry only renders while its periodKey is the current period
 * (see getCurrentDeliverablePeriodKey / fetchProgramDeliverablesBundle), then disappears.
 * @returns {Promise<Array>} the updated periodDeliverables array
 */
export const addAccountPeriodDeliverable = async (accountId, itemId, quantity, periodKey) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to update accounts');
    }
    const qty = Math.max(1, parseInt(String(quantity), 10) || 1);
    if (!itemId || !periodKey) throw new Error('itemId and periodKey are required');
    const accountRef = doc(db, ACCOUNTS_COLLECTION, accountId);
    const snap = await getDoc(accountRef);
    if (!snap.exists()) throw new Error('Account not found');
    const current = snap.data();
    const list = Array.isArray(current.periodDeliverables) ? [...current.periodDeliverables] : [];
    const idx = list.findIndex((e) => e && e.id === itemId && e.periodKey === periodKey);
    if (idx >= 0) {
      list[idx] = { ...list[idx], quantity: (Number(list[idx].quantity) || 0) + qty };
    } else {
      list.push({ id: itemId, quantity: qty, periodKey, addedAt: new Date().toISOString() });
    }
    await updateDoc(accountRef, {
      periodDeliverables: list,
      updatedAt: Timestamp.fromDate(new Date())
    });
    return list;
  } catch (error) {
    console.error('Error adding period deliverable:', error);
    throw error;
  }
};

/**
 * Remove a one-period-only deliverable entry for a specific (itemId, periodKey).
 * @returns {Promise<Array>} the updated periodDeliverables array
 */
export const removeAccountPeriodDeliverable = async (accountId, itemId, periodKey) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to update accounts');
    }
    const accountRef = doc(db, ACCOUNTS_COLLECTION, accountId);
    const snap = await getDoc(accountRef);
    if (!snap.exists()) throw new Error('Account not found');
    const current = snap.data();
    const list = (Array.isArray(current.periodDeliverables) ? current.periodDeliverables : []).filter(
      (e) => !(e && e.id === itemId && e.periodKey === periodKey)
    );
    await updateDoc(accountRef, {
      periodDeliverables: list,
      updatedAt: Timestamp.fromDate(new Date())
    });
    return list;
  } catch (error) {
    console.error('Error removing period deliverable:', error);
    throw error;
  }
};

/**
 * Patch a single deliverable's progress map entry (logs, lastCompleted, etc.).
 * Avoids sending the full account document — prevents races with other updates (e.g. notes auto-save).
 * itemId must not contain '.' (Firestore field-path segment).
 */
export const updateAccountDeliverableSlice = async (accountId, itemId, slice) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to update accounts');
    }
    if (!itemId || String(itemId).includes('.')) {
      throw new Error('Invalid deliverable item id for Firestore map update');
    }
    const accountRef = doc(db, ACCOUNTS_COLLECTION, accountId);
    const cleaned = removeUndefinedValues(slice);
    await updateDoc(accountRef, {
      [`deliverables.${itemId}`]: cleaned,
      updatedAt: Timestamp.fromDate(new Date())
    });
    return cleaned;
  } catch (error) {
    console.error('Error updating deliverable slice:', error);
    throw error;
  }
};

/**
 * Get payments subcollection for an account (Stripe webhook populates this).
 * @param {string} accountId - Account/company ID
 * @returns {Promise<Array>} Payments sorted by createdAt desc
 */
export const getAccountPayments = async (accountId) => {
  try {
    const paymentsRef = collection(db, ACCOUNTS_COLLECTION, accountId, 'payments');
    const q = query(paymentsRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error getting account payments:', error);
    return [];
  }
};

/**
 * Get usage for current period (Stripe webhook populates this).
 * @param {string} accountId - Account/company ID
 * @param {string} periodKey - YYYY-MM-DD format
 * @returns {Promise<Object|null>}
 */
export const getAccountUsage = async (accountId, periodKey) => {
  try {
    const usageRef = doc(db, ACCOUNTS_COLLECTION, accountId, 'usage', periodKey);
    const snap = await getDoc(usageRef);
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (error) {
    console.error('Error getting account usage:', error);
    return null;
  }
};

/**
 * Get Stripe revenue items for FinanceReports (client spending from Stripe).
 * Returns payment records in a format compatible with purchase-based revenue calculations.
 * @returns {Promise<Array>} Array of { createdAt, total, type, program, billingCycle, accountId, company, source: 'stripe' }
 */
export const getStripeRevenueItems = async () => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to get Stripe revenue');
    }
    const accountsRef = collection(db, ACCOUNTS_COLLECTION);
    const q = query(accountsRef, where('type', '==', 'Clients'));
    const accountsSnap = await getDocs(q);
    const revenueItems = [];
    for (const accDoc of accountsSnap.docs) {
      const acc = accDoc.data();
      const accountId = accDoc.id;
      if (!acc.stripeCustomerId && !acc.billing?.stripeSubscriptionId) continue;
      const company = acc.company || acc.contact?.firstName + ' ' + (acc.contact?.lastName || '') || accountId;
      const program = acc.billing?.planKey || acc.program?.status || 'Programs';
      const billingCycle = acc.billing?.billingCycle || 'monthly';
      const paymentsRef = collection(db, ACCOUNTS_COLLECTION, accountId, 'payments');
      const paymentsQ = query(paymentsRef, orderBy('createdAt', 'desc'));
      const paymentsSnap = await getDocs(paymentsQ);
      for (const payDoc of paymentsSnap.docs) {
        const pay = payDoc.data();
        const amount = pay.amount ?? 0;
        if (amount === 0) continue;
        const createdAt = pay.createdAt?.toDate?.() || pay.createdAt || null;
        revenueItems.push({
          id: payDoc.id,
          accountId,
          company,
          total: Math.abs(amount),
          createdAt,
          startDate: createdAt,
          type: 'Programs',
          program: pay.type === 'strategy_call' ? 'A La Carte' : program,
          billingCycle,
          source: 'stripe',
          status: pay.status,
        });
      }
    }
    return revenueItems.sort((a, b) => (new Date(b.createdAt || 0) - new Date(a.createdAt || 0)));
  } catch (error) {
    console.error('Error getting Stripe revenue:', error);
    return [];
  }
};

/**
 * Get Stripe billing snapshot for clients (subscription status, next payment date, etc.)
 * Used by ClientOverview and FinanceReports for display.
 * @returns {Promise<Array>} Array of { accountId, company, billing, ... }
 */
export const getStripeBillingSnapshot = async () => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to get Stripe billing');
    }
    const accountsRef = collection(db, ACCOUNTS_COLLECTION);
    const q = query(accountsRef, where('type', '==', 'Clients'));
    const accountsSnap = await getDocs(q);
    const result = [];
    for (const accDoc of accountsSnap.docs) {
      const acc = accDoc.data();
      if (!acc.stripeCustomerId && !acc.billing?.stripeSubscriptionId) continue;
      const company = acc.company || (acc.contact?.firstName || '') + ' ' + (acc.contact?.lastName || '') || accDoc.id;
      result.push({
        accountId: accDoc.id,
        company,
        billing: acc.billing || {},
        stripeCustomerId: acc.stripeCustomerId,
      });
    }
    return result;
  } catch (error) {
    console.error('Error getting Stripe billing snapshot:', error);
    return [];
  }
};

/**
 * Find account ID by email address
 * @param {string} email - The email address to search for
 * @param {string} [accountType] - Optional account type filter (e.g., 'Leads', 'Clients')
 * @returns {Promise<string|null>} The account ID if found, null otherwise
 */
export const findAccountIdByEmail = async (email, accountType = null) => {
  try {
    if (!email) {
      throw new Error('Email is required');
    }

    // Portal logins may only read their own account (firestore.rules), so the caller's
    // own address resolves through users/{uid}.accountId rather than an email query.
    const me = getAuth().currentUser;
    if (me?.email && me.email.toLowerCase() === String(email).toLowerCase()) {
      const mine = await getMyAccountSnapshot(accountType);
      if (!mine.empty) return mine.docs[0].id;
    }

    const accountsRef = collection(db, ACCOUNTS_COLLECTION);
    
    // First try: search by contact.email field
    let accountsQuery = query(
      accountsRef,
      where('contact.email', '==', email)
    );
    
    // Add type filter if provided
    if (accountType) {
      accountsQuery = query(
        accountsRef,
        where('contact.email', '==', email),
        where('type', '==', accountType)
      );
    }
    
    let accountsSnapshot = await getDocs(accountsQuery);
    
    // If no account found by contact.email, try direct email field
    if (accountsSnapshot.empty) {
      accountsQuery = query(
        accountsRef,
        where('email', '==', email)
      );
      
      if (accountType) {
        accountsQuery = query(
          accountsRef,
          where('email', '==', email),
          where('type', '==', accountType)
        );
      }
      
      accountsSnapshot = await getDocs(accountsQuery);
    }
    
    if (!accountsSnapshot.empty) {
      // Return the first matching account's ID
      const accountId = accountsSnapshot.docs[0].id;
      console.log(`Found account ID ${accountId} for email: ${email}`);
      return accountId;
    }
    
    console.log(`No account found for email: ${email}`);
    return null;
  } catch (error) {
    // A client searching beyond its own account is refused by the rules: not found.
    if (error?.code === 'permission-denied') return null;
    console.error('Error finding account by email:', error);
    throw error;
  }
};

/**
 * The signed-in login's own account as a { empty, docs } result, so call sites that
 * used to find it with an email query keep their shape. Clients may only read the
 * account their users doc links to (firestore.rules), so it is read by id.
 * @param {string} [accountType] - Only return the account if it has this type (e.g. 'Leads')
 * @returns {Promise<{empty: boolean, docs: Array}>}
 */
export const getMyAccountSnapshot = async (accountType = null) => {
  const none = { empty: true, docs: [] };
  const accountId = await getCompanyIdForUser(getAuth().currentUser?.uid);
  if (!accountId) return none;
  try {
    const snap = await getDoc(doc(db, ACCOUNTS_COLLECTION, accountId));
    if (!snap.exists() || (accountType && snap.data().type !== accountType)) return none;
    return { empty: false, docs: [snap] };
  } catch (error) {
    console.error('Error loading own account:', error);
    return none;
  }
};

/**
 * Get the company/account id for a portal user (from users collection).
 * @param {string} userId - Firebase Auth user uid
 * @returns {Promise<string|null>} Account/company id or null
 */
export const getCompanyIdForUser = async (userId) => {
  if (!userId) return null;
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) return null;
    const data = userDoc.data();
    return data.accountId || data.clientId || null;
  } catch (error) {
    console.error('Error getting company id for user:', error);
    return null;
  }
};

/**
 * Portal user docs for an account's primary contact, best match first. Matches email
 * case-insensitively (Firestore equality is exact and CRM contact emails are often
 * capitalized, e.g. "Brentc761@gmail.com" vs the stored "brentc761@gmail.com"), and
 * ranks users linked to this account ahead of stale same-email docs left pointing at a
 * deleted/duplicate account.
 * @param {object} account - Account doc ({ id, contact: { email } })
 * @returns {Promise<Array>} Firestore user doc snapshots
 */
export const findPrimaryPortalUserDocs = async (account) => {
  if (!account?.id) return [];
  const rawEmail = String(account.contact?.email || account.email || '').trim();
  const lowerEmail = rawEmail.toLowerCase();
  if (!lowerEmail) return [];
  const usersRef = collection(db, 'users');
  // Portal logins can't search users by email (firestore.rules: own company only), so for
  // them only the accountId query answers; staff also get the stale same-email docs.
  const snapshots = await Promise.all([
    getDocs(query(usersRef, where('accountId', '==', account.id))),
    ...[...new Set([rawEmail, lowerEmail])].map((email) =>
      getDocs(query(usersRef, where('email', '==', email))).catch(() => ({ docs: [] }))),
  ]);
  const byId = new Map();
  snapshots.forEach((snap) => snap.docs.forEach((d) => byId.set(d.id, d)));
  const rank = (d) => {
    const data = d.data() || {};
    if (data.accountId === account.id || data.clientId === account.id) return 0;
    return data.accountId || data.clientId ? 2 : 1;
  };
  return [...byId.values()]
    .filter((d) => String(d.data()?.email || '').trim().toLowerCase() === lowerEmail)
    .sort((a, b) => rank(a) - rank(b));
};

/**
 * Get the primary (main contact) user id for an account. Used so company members
 * see the same Strategy Calls, Goal Tracking, and Success Manager as the primary.
 * @param {string} accountId - Account/company ID
 * @returns {Promise<string|null>} Primary user's uid or null
 */
export const getPrimaryUserIdForAccount = async (accountId) => {
  if (!accountId) return null;
  try {
    const account = await getAccountById(accountId);
    if (!account) return null;
    const docs = await findPrimaryPortalUserDocs({ ...account, id: accountId });
    return docs[0]?.id || null;
  } catch (error) {
    console.error('Error getting primary user for account:', error);
    return null;
  }
};

// Dashboard metrics functions
export const getAccountsByTypeAndDateRange = async (type, daysAgo) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to get accounts');
    }

    console.log('Getting accounts for type:', type, 'days ago:', daysAgo);
    console.log('Current user ID:', auth.currentUser.uid);

    const accountsRef = collection(db, ACCOUNTS_COLLECTION);
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysAgo);
    
    console.log('Cutoff date:', cutoffDate);
    
    const q = query(
      accountsRef, 
      where('type', '==', type),
      where('createdAt', '>=', Timestamp.fromDate(cutoffDate)),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const results = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      // Convert Firestore Timestamps to JavaScript Dates
      contractStart: doc.data().contractStart?.toDate() || null,
      contractEnd: doc.data().contractEnd?.toDate() || null,
      createdAt: doc.data().createdAt?.toDate() || null,
      updatedAt: doc.data().updatedAt?.toDate() || null
    }));
    
    console.log('Query results:', results);
    return results;
  } catch (error) {
    console.error('Error getting accounts by type and date range:', error);
    throw error;
  }
};

// Integration Management Functions
export const getInternalIntegrations = async (options = {}) => {
  try {
    const docRef = doc(db, 'internalIntegrations', 'dotware');
    // Use getDocFromServer when forceRefresh to bypass cache (e.g. after OAuth callback)
    const docSnap = options.forceRefresh
      ? await getDocFromServer(docRef)
      : await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      // Create initial document if it doesn't exist
      const initialData = {
        connectedIntegrations: {
          twilio: { connected: false, connectedAt: null, authData: {} },

          gdrive: { connected: false, connectedAt: null, authData: {} },
          quickbooks: { connected: false, connectedAt: null, authData: {} },
          zapier: { connected: false, connectedAt: null, authData: {} },
          googleanalytics: { connected: false, connectedAt: null, authData: {} },
          stripe: { connected: false, connectedAt: null, authData: {} },
          meta: { connected: false, connectedAt: null, authData: {} },
          docusign: { connected: false, connectedAt: null, authData: {} },
          youtube: { connected: false, connectedAt: null, authData: {} },
          activecampaign: { connected: false, connectedAt: null, authData: {} },
          googlemaps: { connected: false, connectedAt: null, authData: {} },
          googleCalendar: { connected: false, connectedAt: null, authData: {} },
          gmail: { connected: false, connectedAt: null, authData: {} },
          calendly: { connected: false, connectedAt: null, authData: {} },
          fathom: { connected: false, connectedAt: null, authData: {} }
        }
      };
      
      await setDoc(docRef, initialData);
      return initialData;
    }
  } catch (error) {
    console.error('Error getting internal integrations:', error);
    throw error;
  }
};

export const updateIntegrationStatus = async (integrationKey, status, authData = {}) => {
  try {
    const docRef = doc(db, 'internalIntegrations', 'dotware');
    const updateData = {
      [`connectedIntegrations.${integrationKey}.connected`]: status,
      [`connectedIntegrations.${integrationKey}.connectedAt`]: status ? new Date().toISOString() : null,
      [`connectedIntegrations.${integrationKey}.authData`]: authData
    };
    
    await updateDoc(docRef, updateData);
    return true;
  } catch (error) {
    console.error('Error updating integration status:', error);
    throw error;
  }
};

export const getIntegrationStatus = async (integrationKey) => {
  try {
    const docRef = doc(db, 'internalIntegrations', 'dotware');
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.connectedIntegrations?.[integrationKey] || { connected: false, connectedAt: null, authData: {} };
    }
    
    return { connected: false, connectedAt: null, authData: {} };
  } catch (error) {
    console.error('Error getting integration status:', error);
    throw error;
  }
};

export const disconnectIntegration = async (integrationKey) => {
  try {
    const docRef = doc(db, 'internalIntegrations', 'dotware');
    const updateData = {
      [`connectedIntegrations.${integrationKey}.connected`]: false,
      [`connectedIntegrations.${integrationKey}.connectedAt`]: null,
      [`connectedIntegrations.${integrationKey}.authData`]: {}
    };
    
    await updateDoc(docRef, updateData);
    return true;
  } catch (error) {
    console.error('Error disconnecting integration:', error);
    throw error;
  }
};

// Account-level integration (for portal clients - each account has own QuickBooks)
export const getAccountIntegrations = async (accountId, options = {}) => {
  if (!accountId) return { connectedIntegrations: {} };
  try {
    const docRef = doc(db, 'internalIntegrations', accountId);
    const docSnap = options.forceRefresh
      ? await getDocFromServer(docRef)
      : await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data();
    }
    return { connectedIntegrations: {} };
  } catch (error) {
    console.error('Error getting account integrations:', error);
    throw error;
  }
};

export const disconnectAccountIntegration = async (accountId, integrationKey) => {
  if (!accountId) throw new Error('Account ID required');
  try {
    const docRef = doc(db, 'internalIntegrations', accountId);
    const updateData = {
      [`connectedIntegrations.${integrationKey}.connected`]: false,
      [`connectedIntegrations.${integrationKey}.connectedAt`]: null,
      [`connectedIntegrations.${integrationKey}.authData`]: {}
    };
    await updateDoc(docRef, updateData);
    return true;
  } catch (error) {
    console.error('Error disconnecting account integration:', error);
    throw error;
  }
};

// Purchase Management Functions
export const addPurchase = async (purchaseData) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to add purchases');
    }
    
    const purchasesRef = collection(db, 'clientpurchases');
    
    // Convert all fields to proper Firestore data types
    const formattedData = {
      type: String(purchaseData.type),
      status: String(purchaseData.status || 'Pending'),
      accountId: String(purchaseData.accountId),
      company: String(purchaseData.company),
      contactName: String(purchaseData.contactName || ''),
      total: Number(purchaseData.total) || 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Add type-specific fields
    if (purchaseData.type === 'Programs') {
      formattedData.program = String(purchaseData.program);
      formattedData.billingCycle = String(purchaseData.billingCycle);
      formattedData.startDate = purchaseData.startDate ? Timestamp.fromDate(new Date(purchaseData.startDate)) : null;
      formattedData.endDate = purchaseData.endDate ? Timestamp.fromDate(new Date(purchaseData.endDate)) : null;
    } else if (purchaseData.type === 'A La Carte') {
      formattedData.items = Array.isArray(purchaseData.items) ? purchaseData.items : [];
      formattedData.item = String(purchaseData.item || '');
      formattedData.date = purchaseData.date ? Timestamp.fromDate(new Date(purchaseData.date)) : null;
    }

    console.log('Formatted purchase data for Firestore:', formattedData);
    const docRef = await addDoc(purchasesRef, formattedData);
    
    console.log('✅ New purchase created successfully!');
    console.log('📄 Document ID:', docRef.id);
    
    // Return the complete purchase data with the new ID
    return {
      id: docRef.id,
      ...formattedData,
      // Convert Timestamps back to Dates for the UI
      startDate: formattedData.startDate?.toDate() || null,
      endDate: formattedData.endDate?.toDate() || null,
      date: formattedData.date?.toDate() || null,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  } catch (error) {
    console.error('Error in addPurchase:', error);
    throw error;
  }
};

export const getPurchases = async () => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to get purchases');
    }

    const purchasesRef = collection(db, 'clientpurchases');
    const q = query(
      purchasesRef, 
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      // Convert Firestore Timestamps to JavaScript Dates
      startDate: doc.data().startDate?.toDate() || null,
      endDate: doc.data().endDate?.toDate() || null,
      date: doc.data().date?.toDate() || null,
      createdAt: doc.data().createdAt?.toDate() || null,
      updatedAt: doc.data().updatedAt?.toDate() || null
    }));
  } catch (error) {
    console.error('Error getting purchases:', error);
    throw error;
  }
};

export const updatePurchase = async (purchaseId, purchaseData) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to update purchases');
    }
    
    const purchaseRef = doc(db, 'clientpurchases', purchaseId);
    
    // Convert all fields to proper Firestore data types
    const formattedData = {
      type: String(purchaseData.type),
      status: String(purchaseData.status),
      accountId: String(purchaseData.accountId),
      company: String(purchaseData.company),
      contactName: String(purchaseData.contactName || ''),
      total: Number(purchaseData.total) || 0,
      updatedAt: serverTimestamp()
    };

    // Add type-specific fields
    if (purchaseData.type === 'Programs') {
      formattedData.program = String(purchaseData.program);
      formattedData.billingCycle = String(purchaseData.billingCycle);
      formattedData.startDate = purchaseData.startDate ? Timestamp.fromDate(new Date(purchaseData.startDate)) : null;
      formattedData.endDate = purchaseData.endDate ? Timestamp.fromDate(new Date(purchaseData.endDate)) : null;
    } else if (purchaseData.type === 'A La Carte') {
      formattedData.items = Array.isArray(purchaseData.items) ? purchaseData.items : [];
      formattedData.item = String(purchaseData.item || '');
      formattedData.date = purchaseData.date ? Timestamp.fromDate(new Date(purchaseData.date)) : null;
    }

    console.log('Updating purchase with data:', formattedData);
    await updateDoc(purchaseRef, formattedData);
    
    console.log('✅ Purchase updated successfully!');
    
    // Return the updated purchase data
    return {
      id: purchaseId,
      ...formattedData,
      // Convert Timestamps back to Dates for the UI
      startDate: formattedData.startDate?.toDate() || null,
      endDate: formattedData.endDate?.toDate() || null,
      date: formattedData.date?.toDate() || null,
      updatedAt: new Date()
    };
  } catch (error) {
    console.error('Error in updatePurchase:', error);
    throw error;
  }
};

export const deletePurchase = async (purchaseId) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to delete purchases');
    }
    
    const purchaseRef = doc(db, 'clientpurchases', purchaseId);
    await deleteDoc(purchaseRef);
    
    console.log('✅ Purchase deleted successfully!');
    return purchaseId;
  } catch (error) {
    console.error('Error in deletePurchase:', error);
    throw error;
  }
};

/**
 * Get flagged leads that need contact
 * @returns {Promise<Array>} Array of flagged lead objects
 */
export const getFlaggedLeads = async () => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to get flagged leads');
    }

    // Get all leads
    const accountsRef = collection(db, ACCOUNTS_COLLECTION);
    const q = query(
      accountsRef,
      where('type', '==', 'Leads')
    );
    
    const querySnapshot = await getDocs(q);
    const leads = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || null,
      updatedAt: doc.data().updatedAt?.toDate() || null
    }));

    // Helper function to convert timestamp to Date
    const toDate = (timestamp) => {
      if (!timestamp) return null;
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        return timestamp.toDate();
      }
      if (typeof timestamp === 'string') {
        return new Date(timestamp);
      }
      if (timestamp instanceof Date) {
        return timestamp;
      }
      if (typeof timestamp === 'number') {
        return new Date(timestamp);
      }
      return null;
    };

    // Helper function to calculate days since last contact note
    const getDaysSinceLastContact = (account) => {
      if (!account || !account.notes || !Array.isArray(account.notes)) {
        return null;
      }

      const contactNotes = account.notes.filter(note => note.contact === true);
      if (contactNotes.length === 0) {
        return null;
      }

      // Sort by timestamp descending to get most recent
      const sortedNotes = contactNotes.sort((a, b) => {
        const dateA = toDate(a.timestamp);
        const dateB = toDate(b.timestamp);
        if (!dateA) return 1;
        if (!dateB) return -1;
        return dateB - dateA;
      });

      const mostRecentNote = sortedNotes[0];
      const noteDate = toDate(mostRecentNote.timestamp);
      if (!noteDate) {
        return null;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      noteDate.setHours(0, 0, 0, 0);
      const diffTime = today - noteDate;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      return diffDays >= 0 ? diffDays : 0;
    };

    // Filter flagged leads
    const flaggedLeads = leads.filter(lead => {
      if (!lead || lead.type !== 'Leads') {
        return false;
      }

      // Don't flag lost leads
      if (lead.stage === 'Lost' || lead.result === 'Lost') {
        return false;
      }

      const daysUntilFlagged = CONTACT_FLAG_THRESHOLD_DAYS;

      const daysSinceContact = getDaysSinceLastContact(lead);

      // If no contact notes, check days since creation
      if (daysSinceContact === null) {
        if (!lead.createdAt) {
          return false;
        }

        const createdDate = toDate(lead.createdAt);
        if (!createdDate) {
          return false;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        createdDate.setHours(0, 0, 0, 0);
        const diffTime = today - createdDate;
        const daysSinceCreation = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        return daysSinceCreation >= daysUntilFlagged;
      }

      // Check if days since contact exceeds threshold
      return daysSinceContact >= daysUntilFlagged;
    });

    return flaggedLeads;
  } catch (error) {
    console.error('Error getting flagged leads:', error);
    throw error;
  }
};

const GLOBAL_DOCUMENTS_COLLECTION = 'globalDocuments';

/** Shared, cross-company document library. Visible to every client and internal user. Sorted newest first. */
export const getGlobalDocuments = async () => {
  try {
    const snapshot = await getDocs(collection(db, GLOBAL_DOCUMENTS_COLLECTION));
    const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    return docs.sort((a, b) => new Date(b.uploadDate || 0) - new Date(a.uploadDate || 0));
  } catch (error) {
    console.error('Error getting global documents:', error);
    throw error;
  }
};

/** Add a document to the shared global library. Returns the new doc id. */
export const addGlobalDocument = async (docData) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to post global documents');
    }
    const docRef = await addDoc(collection(db, GLOBAL_DOCUMENTS_COLLECTION), {
      ...docData,
      createdAt: serverTimestamp()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding global document:', error);
    throw error;
  }
};

/** Remove a document from the shared global library. */
export const deleteGlobalDocument = async (id) => {
  try {
    await deleteDoc(doc(db, GLOBAL_DOCUMENTS_COLLECTION, id));
  } catch (error) {
    console.error('Error deleting global document:', error);
    throw error;
  }
};